import { useEffect, useState } from 'react';

import { useCreateFeedback } from '@/hooks/useSwr';
import useToastOn429Error from '@/hooks/useToastOn429Error';
import {
  FeedbackSchema,
  PatchFeedbackBody,
  PostFeedbackBody,
} from '@/types/schema';
import { apiFeedbackPath } from '@/utils/constants';

export type FeedbackDiff = Pick<
  PostFeedbackBody,
  'score' | 'value' | 'feedback_config' | 'comment'
>;

export function useFeedbackCrud({
  runId,
  feedback_key,
  existingFeedback,
  onSuccess,
  onError,
  runRuleId,
}: {
  runId: string;
  feedback_key: string;
  existingFeedback?: FeedbackSchema;
  onSuccess?: (deleted: boolean) => void;
  onError?: (error: Error) => void;
  runRuleId?: string;
}) {
  const mutate = useCreateFeedback();
  const { handle429Error } = useToastOn429Error();
  const [feedback, setFeedback] = useState(existingFeedback);

  useEffect(() => {
    setFeedback(existingFeedback);
  }, [existingFeedback]);

  const onSubmit = (value: FeedbackDiff) => {
    if (feedback?.id != null) {
      if (
        (value.score != null && value.score === feedback.score) ||
        (value.value != null && value.value === feedback.value) ||
        (value.comment != null && value.comment === feedback.comment)
      ) {
        return Promise.resolve(feedback);
      }
      return mutate
        .trigger({
          url: `${apiFeedbackPath}/${feedback.id}`,
          method: 'PATCH',
          json: { ...value } satisfies PatchFeedbackBody,
        })
        .then((feedback) => {
          onSuccess?.(false);
          setFeedback(feedback);
          return feedback;
        })
        .catch((e) => {
          onError?.(e);
          handle429Error(e, 'feedback');
          return undefined;
        });
    }

    return mutate
      .trigger({
        json: {
          ...value,
          key: feedback_key,
          run_id: runId,
          feedback_source: {
            type: 'app',
            ...(runRuleId ? { metadata: { rule_id: runRuleId } } : {}),
          },
        } satisfies PostFeedbackBody,
      })
      .then((feedback) => {
        onSuccess?.(false);
        setFeedback(feedback);
        return feedback;
      })
      .catch((e) => {
        onError?.(e);
        handle429Error(e, 'feedback');
        return undefined;
      });
  };

  const onDelete = () => {
    if (feedback?.id == null) {
      // reset errors if just deleting w/out submission
      mutate.reset();

      setFeedback(undefined);
      return Promise.resolve(undefined);
    }

    return mutate
      .trigger({
        url: `${apiFeedbackPath}/${feedback.id}`,
        method: 'DELETE',
      })
      .then(() => {
        onSuccess?.(true);
        setFeedback(undefined);
        return undefined;
      })
      .catch((e) => {
        onError?.(e);
        return undefined;
      });
  };
  return {
    feedback,
    onSubmit,
    onDelete,
    error: mutate.error,
    reset: mutate.reset,
    isMutating: mutate.isMutating,
  };
}
